
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

interface ProtectedRouteProps {
  children: React.ReactNode;
  /**
   * Gate the route on admin, not just on being signed in. Without this an
   * admin page mounts and starts its data loads before any in-page isAdmin
   * check can redirect — this stops the component rendering at all.
   */
  requiresAdmin?: boolean;
}

const ProtectedRoute = ({ children, requiresAdmin = false }: ProtectedRouteProps) => {
  const { currentUser, loading, isAdmin } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    if (!loading && !currentUser) {
      navigate("/login", {
        state: {
          redirect: `${window.location.pathname}${window.location.search}${window.location.hash}`,
        },
      });
    }
  }, [currentUser, loading, navigate]);

  useEffect(() => {
    // The one admin guard for every admin page (they no longer each repeat it): tell the person why, once,
    // then leave. The backend enforces admin on every admin endpoint regardless -- this is only the UI.
    if (!loading && currentUser && requiresAdmin && !isAdmin) {
      toast({
        title: "Access Denied",
        description: "You don't have admin access. Please contact an administrator.",
        variant: "destructive",
      });
      navigate("/dashboard", { replace: true });
    }
  }, [currentUser, isAdmin, loading, navigate, requiresAdmin, toast]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-4 border-primary rounded-full border-t-transparent animate-spin"></div>
      </div>
    );
  }

  if (!currentUser) return null;
  if (requiresAdmin && !isAdmin) return null;

  return <>{children}</>;
};

export default ProtectedRoute;
